import {
	Card,
	CardActionArea,
	CardContent,
	Grid,
	Paper,
	Typography,
	Tooltip,
	Chip,
	CardActions,
	Stack,
} from '@mui/material';

import { useCallback, useState } from 'react';

import { useSelector } from 'react-redux';

import { useNavigate } from 'react-router-dom';

import { v4 as uuidv4 } from 'uuid';

import { ReactComponent as FavoriteIcon } from 'common/assets/images/favorite.svg';
import { ReactComponent as UnFavoriteIcon } from 'common/assets/images/unfavorite.svg';
import ChipComponent from 'common/components/Chip/Chip';
import AlertComponent from 'common/components/ToastMessage/ToastMessage';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { rrcpDashboardRoutes } from 'common/constants/rrcp-dashboard-routes';
import {
	APP_GENERAL_ERROR_MSG,
	NO_DATA_FOUND,
} from 'common/constants/TextContent';
import { RootState } from 'common/store/AppStore';
import { dateFullYearFormat, urlWithTenant } from 'common/utils/utilFunctions';

import { favoriteReport } from '../api';
import { IFavoriteParams, IReportingCardData } from '../api/type-def';

import styles from './ReportingCard.module.scss';

const { OK } = HTTP_STATUS_CODES;

const { reporting, reportingLibraryDetail } = rrcpDashboardRoutes;

interface IReportingCardProps {
	reportingCards: IReportingCardData[] | null;
	fetchReportsData: (params: any) => void;
}

const ReportingCard = (props: IReportingCardProps) => {
	const { reportingCards, fetchReportsData } = props;

	const navigate = useNavigate();

	const [isToastOpen, setIsToastOpen] = useState(false);
	const [toastMessage, setToastMessage] = useState('');

	const loggedInUser = useSelector(
		(state: RootState) => state?.common?.loggedInUser,
	);

	const onCardClick = (card: IReportingCardData) => {
		const { reportId } = card;
		navigate(
			urlWithTenant(`/${reporting}/${reportingLibraryDetail}/${reportId}`),
			{
				state: {
					reportName: card.reportName,
				},
			},
		);
	};

	const handleToast = useCallback(() => {
		setIsToastOpen(false);
		setToastMessage('');
	}, []);

	const toggleFavorite = async (card: IReportingCardData) => {
		const requestBody: IFavoriteParams = {
			reportId: card.reportId,
			favourite: !card.isFavourite,
			userId: loggedInUser?.userId ?? '',
		};
		try {
			const resp = await favoriteReport(requestBody);
			if (resp?.statusCode === OK) {
				const pageParams = {
					pageNumber: 0,
					pageSize: 10,
				};
				fetchReportsData(pageParams);
			} else {
				setIsToastOpen(true);
				setToastMessage(resp?.errorBody?.errorMessage ?? APP_GENERAL_ERROR_MSG);
			}
		} catch (ex) {
			setIsToastOpen(true);
			setToastMessage(APP_GENERAL_ERROR_MSG);
		}
	};

	const renderCategories = (categories: string[]) => {
		if (categories.length > 2) {
			return (
				<>
					{categories.slice(0, 2).map((category: string) => (
						<ChipComponent
							variant="filled"
							key={uuidv4()}
							label={category}
							defaultClass
							className={styles['reporting-card-category']}
						/>
					))}
					<Tooltip
						title={categories.slice(2).join(', ')}
						arrow
						placement="right"
						key={uuidv4()}
					>
						<Chip
							variant="filled"
							key={uuidv4()}
							label={`+${categories.length - 2}`}
							className={styles['reporting-card-category-more']}
						/>
					</Tooltip>
				</>
			);
		}
		return (
			<>
				{categories.map((category: string) => (
					<ChipComponent
						variant="filled"
						key={uuidv4()}
						label={category}
						defaultClass
						className={styles['reporting-card-category']}
					/>
				))}
			</>
		);
	};

	return (
		<div>
			{!reportingCards?.length ? (
				<Paper className={styles['common-table-box']}>{NO_DATA_FOUND}</Paper>
			) : (
				<Grid container className={styles['reporting-card-groups']}>
					{reportingCards?.map((card: IReportingCardData) => (
						<Grid
							item
							key={uuidv4()}
							xs={12}
							sm={6}
							md={4}
							lg={3}
							xl={3}
							className={styles['reporting-card-row']}
						>
							<Card className={styles['reporting-card-box']}>
								<CardActionArea
									onClick={() => onCardClick(card)}
									data-testid={`reporting-card-${card.reportId}`}
								>
									<CardContent>
										<Stack direction="row">
											<img
												src={card.dashboardUrl}
												alt="img"
												draggable={false}
												className={styles['reporting-card-image']}
											/>
										</Stack>
										<Stack
											direction="row"
											className={styles['reporting-card-header']}
										>
											<Typography className={styles['reporting-card-title']}>
												{card.reportName}
											</Typography>
										</Stack>
										<Stack
											direction="row"
											className={styles['reporting-card-categories']}
										>
											{renderCategories(card.categories)}
										</Stack>
									</CardContent>
								</CardActionArea>
								<CardActions>
									<Stack
										direction="row"
										className={styles['reporting-card-metadata']}
									>
										<div className={styles['reporting-card-last-updated']}>
											<Typography className={styles['reporting-card-date']}>
												Last Updated: {dateFullYearFormat(card.lastUpdatedDate)}
											</Typography>
										</div>
										<div className={styles['reporting-card-favorites']}>
											{!card.isFavourite ? (
												<UnFavoriteIcon
													onClick={() => toggleFavorite(card)}
													aria-label="Unfavorite Icon"
												/>
											) : (
												<FavoriteIcon
													onClick={() => toggleFavorite(card)}
													aria-label="Favorite Icon"
												/>
											)}
										</div>
									</Stack>
								</CardActions>
							</Card>
						</Grid>
					))}
				</Grid>
			)}
			<AlertComponent
				open={isToastOpen}
				toastMessage={toastMessage}
				severity="error"
				handleCloseMessage={handleToast}
				vertical="top"
				horizontal="right"
			/>
		</div>
	);
};

export default ReportingCard;
